import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, type ApiClient } from '../api/client';
import { appendRequestId } from '../utils/errors';
import PersonalAgentAccess, { type PersonalAgentAccessRequest } from './PersonalAgentAccess';
import PersonalAgentConversation from './PersonalAgentConversation';
import './PersonalAgentPage.css';

export type PersonalAgentSnapshot = {
  configured: boolean; status: string; conversation_id?: string | null;
  profile_id?: string;
  setup_required?: boolean; legacy_configured?: boolean;
  gateway_ready?: boolean; readiness_error?: string | null;
  delegation_enabled?: boolean;
  model_allowance?: { expires_at: string | null; max_requests: number; max_tokens: number; future_max_tokens?: number; max_cost: number; automatic_renewal: boolean; period?: string } | null;
  access_requests?: PersonalAgentAccessRequest[];
  thread?: { paused: boolean; has_earlier?: boolean;
    events: Array<{ id: string; request: string; state: string; created_at?: number;
      receipt: { text?: string; error?: string; status?: string } | null;
      effects: Array<{ call_id: string; tool: string; result: unknown | null }> }>;
    children: Array<{ conversation_id: string; completion_observed: boolean }>;
  };
};

export default function PersonalAgentPage({ apiClient, isAdmin = false, apiPrefix = '/api/personal-agent' }: { apiClient: ApiClient; isAdmin?: boolean; apiPrefix?: '/api/personal-agent' | '/api/assistant' }) {
  const toolsOnly = apiPrefix === '/api/assistant';
  const [snapshot, setSnapshot] = useState<PersonalAgentSnapshot | null>(null);
  const [request, setRequest] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // A lost response retains the ID; retry rediscovers, never replays, work.
  const submission = useRef<{ id: string; request: string } | null>(null);
  const refreshGeneration = useRef(0);
  const refresh = useCallback(async (signal?: AbortSignal) => {
    const generation = ++refreshGeneration.current;
    const next = await apiClient.get<PersonalAgentSnapshot>(apiPrefix, { signal });
    if (generation === refreshGeneration.current) setSnapshot(next);
  }, [apiClient, apiPrefix]);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try { await refresh(controller.signal); }
      catch { if (!controller.signal.aborted) setError('Personal-agent status is unavailable.'); }
      if (!controller.signal.aborted) timer = setTimeout(() => { void poll(); }, 5000);
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [refresh]);
  const submit = async () => {
    if (busy || !request.trim()) return;
    setBusy(true); setError(null);
    if (!submission.current || submission.current.request !== request) submission.current = { id: crypto.randomUUID(), request };
    try {
      const sent = submission.current;
      await apiClient.post(`${apiPrefix}/requests`, sent);
      setSnapshot(current => current?.thread ? { ...current, thread: { ...current.thread,
        events: current.thread.events.some(event => event.id === sent.id) ? current.thread.events : [...current.thread.events,
          { id: sent.id, request: sent.request, state: 'queued', receipt: null, effects: [] }] } } : current);
      submission.current = null; setRequest('');
      try { await refresh(); }
      catch { setError('Message saved. Reconnecting to check the reply…'); }
    } catch { setError('Request not confirmed. Retry the unchanged request to check its saved event. Completed actions are never automatically replayed.'); }
    finally { setBusy(false); }
  };
  const pause = async () => {
    setBusy(true); setError(null);
    try { await apiClient.post(`${apiPrefix}/lifecycle`, { paused: !snapshot?.thread?.paused }); await refresh(); }
    catch { setError('Lifecycle change not confirmed. Refresh to check its saved state.'); }
    finally { setBusy(false); }
  };
  const start = async () => {
    setBusy(true); setError(null);
    try {
      try { await apiClient.post(`${apiPrefix}/start`, {}); }
      catch (failure) {
        const message = failure instanceof ApiError && failure.status === 503
          ? 'Assistant setup is unavailable on the server. Server configuration or runtime delivery needs attention before Start can succeed.'
          : failure instanceof ApiError && failure.status === 403
            ? 'Assistant start was denied. Your account’s permission to start this assistant needs review.'
            : failure instanceof ApiError && failure.status === 401
              ? 'Assistant start requires a valid signed-in session. Sign-in recovery did not confirm this request.'
            : 'Assistant start was not confirmed. Its saved state needs checking before another Start.';
        setError(appendRequestId(`${message} You do not need to enter credentials or link a conversation.`,
          failure instanceof ApiError ? failure.requestId : undefined));
        return;
      }
      try { await refresh(); }
      catch {
        setError('Start was accepted, but assistant status could not be refreshed. Setup completion is not yet confirmed.');
      }
    }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true); setError(null);
    try { await apiClient.post(`${apiPrefix}/stop`, {}); await refresh(); }
    catch { setError('Revocation not confirmed. Refresh to check the saved status.'); }
    finally { setBusy(false); }
  };
  const status = snapshot?.status ?? 'checking';
  const statusLabel = ({ checking: 'Connecting…', unconfigured: 'Not started', working: 'Thinking…',
    awaiting_permission: 'Waiting for permission', queued: 'Message queued', paused: 'Paused', revoked: 'Access revoked',
    held: 'Needs attention', waiting: snapshot?.thread?.events.at(-1)?.state === 'halted' ? 'Reply failed' : 'Ready'
  } as Record<string, string>)[status] ?? 'Needs attention';
  return <main className={`page personal-agent-page${snapshot?.configured ? ' personal-agent-page--chat' : ''}`}>
    <header className="personal-agent-heading"><div><h1>Personal agent</h1>
      <p>One continuous conversation. Your history stays here.</p></div>
      <p role="status">{toolsOnly ? statusLabel : `Status: ${status}`}</p>
    </header>
    {error && <p role="alert">{error}</p>}
    {snapshot && !snapshot.configured && <section className="card">
        {snapshot.setup_required && <p>{toolsOnly
          ? 'An administrator needs to enable personal assistants in Settings → Personal agent before you can start.'
          : 'An administrator needs to approve the assistant runtime and model allowance in Settings → Personal agent → Optional VM runtime before you can start.'}</p>}
        <p>Start your assistant to create its own private history using the approved model connection. This page does not collect tokens or ask you to link a conversation.</p>
        <p>Start allows use of the assistant’s own history and approved model within the server-approved spending and credential limits. {toolsOnly ? 'The approved daily allowance renews each UTC day. The assistant may find titles of your accessible conversations; reading their messages requires confirmation for that task.' : 'Reading other conversations requires a separate task-specific confirmation.'}</p>
        <button type="button" disabled={busy || snapshot.setup_required === true} onClick={() => { void start(); }}>Start assistant</button>
      </section>}
    {snapshot?.configured && <>
      <details className="personal-agent-controls"><summary>Assistant controls</summary><section className="card">
        {isAdmin && <p><Link to={toolsOnly ? '/settings/personal-agent' : '/settings/personal-agent/legacy'}>Personal agent settings</Link></p>}
        {snapshot.status === 'preparing' && <p>Your private gateway is being prepared. Its saved conversation will remain here.</p>}
        {snapshot.status === 'provisioning_unknown' && <p>Provisioning could not be confirmed. No replacement key or automatic retry will be issued; server-side review is required.</p>}
        {snapshot.status === 'held' && <p>The assistant is held. Start or Resume cannot clear a revocation, expired allowance, or spending limit.</p>}
        {snapshot.model_allowance && <p>Model allowance: at most {snapshot.model_allowance.max_requests} requests, {snapshot.model_allowance.max_tokens} tokens and {snapshot.model_allowance.max_cost} cost units. {snapshot.model_allowance.period ? `The allowance renews each ${snapshot.model_allowance.period}. No model requests run while idle.` : <>Credential expiry: {snapshot.model_allowance.expires_at ?? 'not issued'}. {snapshot.model_allowance.automatic_renewal ? 'Automatic renewal is approved.' : 'Automatic renewal is not enabled.'} These limits do not refill.</>}</p>}
        {snapshot.model_allowance?.future_max_tokens !== undefined && snapshot.model_allowance.future_max_tokens !== snapshot.model_allowance.max_tokens
          && <p>New daily allowances use {snapshot.model_allowance.future_max_tokens} tokens. Today's issued allowance keeps its original limit and spending.</p>}
        <p>{toolsOnly ? 'Connection' : 'Gateway'}: {snapshot.gateway_ready ? (toolsOnly ? 'ready' : 'reachable (provider and executor success not yet proven)') : `unavailable${snapshot.readiness_error ? ` — ${snapshot.readiness_error}` : ''}`}</p>
        <button type="button" disabled={busy} onClick={() => { void pause(); }}>{snapshot.thread?.paused ? 'Resume agent' : 'Pause agent'}</button>
        <p>Pause prevents new wakeups and tool effects. An already admitted action may finish.</p>
        <button type="button" disabled={busy} onClick={() => { void revoke(); }}>Revoke assistant access</button>
      </section></details>
    </>}
    {!snapshot?.configured && isAdmin && <p><Link to={toolsOnly ? '/settings/personal-agent' : '/settings/personal-agent/legacy'}>Personal agent settings</Link></p>}
    {toolsOnly && snapshot?.legacy_configured && <p><Link to="/personal-agent/legacy">Earlier assistant history</Link></p>}
    {snapshot?.configured && <>
      <PersonalAgentAccess requests={snapshot.access_requests ?? []} apiClient={apiClient} refresh={refresh} apiPrefix={apiPrefix} allowOngoing={!toolsOnly} />
      <PersonalAgentConversation key={`${apiPrefix}:${snapshot.profile_id ?? snapshot.conversation_id ?? 'personal'}`}
        thread={snapshot.thread ?? { paused: false, events: [], children: [] }} apiClient={apiClient} apiPrefix={apiPrefix}
        request={request} setRequest={setRequest} submit={() => { void submit(); }} busy={busy} ready={!!snapshot.gateway_ready} />
      {(snapshot.delegation_enabled || !!snapshot.thread?.children.length) && <section className="card" aria-label="Delegated tasks"><h2>Delegated tasks</h2>
        {!snapshot.delegation_enabled && <p>Delegation is not enabled. Creating and operating child conversations needs an explicit app-bound permission; existing conversation tokens do not gain that permission automatically.</p>}
        {!snapshot.thread?.children.length && <p>No delegated tasks yet. Substantial work belongs in ordinary Mission Control conversations.</p>}
        {snapshot.thread?.children.map(child => <p key={child.conversation_id}>
          <Link to={`/conversations/${child.conversation_id}`}>Open task conversation</Link> — {child.completion_observed ? 'completion observed' : 'awaiting completion'}
        </p>)}
      </section>}
    </>}
  </main>;
}
